define((require) => {
    "use strict";

    const $                             = require("jquery");
    const {
        defer, clone, keys, pick
    }    = require("underscore");
    const Marionette                    = require("marionette");
    const {isBlank}                     = require("shared/util/StringUtils");
    const TextEditorConstants           = require("ui/experience/widgets/textWidget/textEditor/TextEditorConstants");
    const TextEditorView                = require("ui/experience/widgets/textWidget/textEditor/TextEditorView");
    const TextEditorModel               = require("ui/experience/widgets/textWidget/textEditor/TextEditorModel");

    /**
     * A controller that correlates between text widget and text editor
     *
     *  TextEditorController
     */
    return Marionette.Controller.extend({
        /**
         * The text widget element as jQuery object
         * @private
         */
        $el: null,

        /**
         * The text widget model
         * @private
         */
        _textWidgetModel: null,

        /**
         * The text widget view that embeds the actual text editor
         * @private
         */
        _textWidgetView: null,

        /**
         * Initialization
         * @param {TextWidgetView} textWidgetView   The text widget view
         */
        initialize(textWidgetView) {
            // We receive the TextWidgetView through TransformableTextWidget. This pretty much just appends a few things to the model.
            this._textWidgetView = textWidgetView;
            this._textWidgetModel = this._textWidgetView.model;
            this.$el = this._textWidgetView.$el;

            this._editorModel = new TextEditorModel();
            this.updateEditorModelFromWidgetModel();
            this._editorView = new TextEditorView({
                viewModel: this.options.viewModel,
                element: this.$el,
                model: this._editorModel,
                textWidgetView: this._textWidgetView
            });

            // This will trigger the textWidgetView to re-render, which in turn
            // will call this class and end up that we create two ckeditors,
            // therefore lets be silent
            this._textWidgetModel.set("editorModel", this._editorModel, {silent: true});
            this.listenTo(this._editorModel, "change", this._modelChangeHandler);
            this.listenTo(this._editorModel, "change:fontSize", this._reposition);

            this.listenTo(this._editorView, {
                "contentChange": this._contentChangeHandler,
                "mouseup": this._updateEditorModel,
                "arrowKeyUp": this._updateEditorModel,
                "editorBlur": this.saveModel,
                "newParagraph": this._updateEditorModel
            });

            this.listenTo(this._textWidgetView, "render", this._editorView.ieQuoteHack);
            this.listenTo(this._textWidgetModel, "dropped:to:scene", this._onWidgetDrop);
        },

        /**
         * Trigger update in order to update the free transform handlers
         *
         * In addition, the content change will possibly affect the height of the widget.
         * we need to trigger change of height on the model in order to update the vertical scroll bar, if needed
         *
         * @private
         */
        _contentChangeHandler() {
            this._textWidgetView.trigger("update");
            this._textWidgetModel.trigger("change:height");
        },

        _updateEditorModel() {
            // When mouse up, some times the selection is still the previous one, so we need to defer.
            defer(() => {
                // Be careful when using #defer - there's a chance we're 'closed' (i.e. deleted) at this point
                if (!this._editorModel) {
                    return;
                }

                const attributes = this._editorView.getCss([
                    "color", "fontSize", "fontFamily",
                    "fontWeight", "fontStyle", "lineHeight",
                    "textAlign", "letterSpacing", "textDecoration",
                    "textTransform", "fontVariant", "textShadow"
                ]);

                // Text decoration is not inherited so we pull in the widget text decoration if it's defined on the model
                if ((attributes.textDecoration === "none") && this._textWidgetModel.get("textDecoration") !== "none") {
                    attributes.textDecoration = this._textWidgetModel.get("textDecoration");
                }

                attributes.nodeName = this._editorView.getNodeName();

                // Pass cssUpdate to stop the circular callbacks. This shall prevent triggering the _modelChangeHandler
                // in this class. see {@Link #_modelChangeHandler()} below
                this._editorModel.set(attributes, {"cssUpdate": false});
            });
        },

        /**
         * Copy values from textWidgetModel to editorModel so that the property view
         * reflects the attributes of textWidgetModel.
         */
        updateEditorModelFromWidgetModel() {
            const attrs = pick(this._textWidgetModel.attributes, keys(TextEditorModel.prototype.defaults));

            // Pass cssUpdate to stop the cyclic callbacks.
            this._editorModel.set(attrs, {"cssUpdate": false});
        },

        /*
         * Update the view when model changes. If the editor is not editable, we apply changes to the whole
         * widget. Otherwise, we apply the changes to the text selection
         */
        _modelChangeHandler(model, options) {
            const changed = clone(model.changed);

            if (options.cssUpdate === false) {
                return;
            }

            if (this.getViewEditable()) {
                this._editorView.applyStyle(changed, model);
            } else {
                if (changed.hasOwnProperty("nodeName")) {
                    const nodeName = changed.nodeName || TextEditorConstants.TAGS.DIV;
                    this._textWidgetView.$el.children().each((i, childEl) => {
                        const $childEl = $(childEl);
                        // Make sure the styles are applied to the new node type as well.
                        $childEl.replaceWith(`<${nodeName} style="${$childEl.attr("style")}">${childEl.innerHTML}</${nodeName}>`);
                    });
                } else {
                    this._textWidgetModel.set(changed);
                    this._editorView.removeStyle(keys(changed));
                }

                this.saveModel();
            }
        },

        /**
         * When the font size is set from very big to small, the widget may
         * disappear out of the top. This check prevents this behaviour.
         */
        _reposition() {
            // Find the top coordinate of the widget in pixels.
            const experience = this.options.viewModel.get("experience");
            const top = experience.getPixelPosition({
                top: this._textWidgetModel.get("top")
            }).top;

            // Wait for FreeTransform to do its resizing before we calculate
            // the new height.
            defer(() => {
                const height = this.$el.height();

                if (top + height < 0) {
                    this._textWidgetModel.set("top", 5 - height);
                }
            });
        },

        enableEditable() {
            this._editorView.enableEditable();
        },

        disableEditable() {
            this._editorView.disableEditable();
        },

        getViewEditable() {
            return this._editorView.isEditable;
        },

        _onWidgetDrop() {
            defer(() => {
                this._editorView.editAllText();
            });
        },

        saveModel() {
            const data = this._editorView.getContent();
            const elements = this.$el.get();

            if (isBlank(data)) {
                return this._textWidgetModel.destroy();
            }

            const fontFamilies = this._editorView.parseFont(elements);

            // The {render: false} is to avoid loss of focus when selecting text and interacting
            // with the properties view (selecting new font, etc).
            this._textWidgetModel.set({
                text: data,
                fontFamilies
            }, {render: false});

            this._textWidgetModel.trigger("content:updated");
        },

        onClose() {
            this._editorModel.destroy();
            this._editorModel = null;
            this._editorView.close();
        }
    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/experience/widgets/textWidget/textEditor/TextEditorController.js